<template>
    <el-dropdown
        trigger="click"
        placement="bottom-end"
        @command="handleProjectDropdownCommand"
    >
        <button
            type="button"
            class="flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 bg-[rgba(24,24,27,0.80)] hover:bg-gray-700/50 transition-colors duration-200 max-w-[220px]"
        >
            <span class="text-white text-sm truncate">
                {{ currentProjectName }}
            </span>
            <svg
                class="w-4 h-4 text-white"
                viewBox="0 0 20 20"
                fill="currentColor"
            >
                <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                />
            </svg>
        </button>

        <template #dropdown>
            <el-dropdown-menu class="min-w-[240px]">
                <el-dropdown-item
                    v-for="p in filteredProjectList"
                    :key="p.id"
                    :command="{
                        type: 'select',
                        project: p,
                    }"
                >
                    <div class="flex items-center justify-between gap-3 w-full">
                        <span class="truncate">
                            {{ p.name }}
                        </span>
                        <span
                            v-if="appStore.currentProject?.id === p.id"
                            class="text-text-active"
                            >✓</span
                        >
                    </div>
                </el-dropdown-item>

                <el-dropdown-item divided :command="{ type: 'add' }">
                    {{ t('batchTools.addProject') }}
                </el-dropdown-item>

                <el-dropdown-item
                    :command="{ type: 'edit' }"
                    :disabled="!appStore.currentProject"
                >
                    {{ t('batchTools.editProject') }}
                </el-dropdown-item>

                <el-dropdown-item
                    :command="{ type: 'delete' }"
                    :disabled="
                        !appStore.currentProject ||
                        filteredProjectList.length <= 1
                    "
                >
                    {{ t('batchTools.delete') }}
                </el-dropdown-item>
            </el-dropdown-menu>
        </template>
    </el-dropdown>

    <!-- add/edit modal (aligned with ProjectTab.vue) -->
    <transition v-if="dialogVisible" name="fade">
        <div
            class="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black/50 backdrop-blur-sm z-[60]"
            @click.self="dialogVisible = false"
        >
            <div
                class="flex flex-col relative p-4 w-[92vw] max-w-[500px] h-[300px] bg-setting-bg rounded-lg items-center justify-center"
            >
                <div
                    class="flex w-full p-4 items-center justify-between absolute top-0 left-0 right-0"
                >
                    <h2 class="text-white text-lg font-medium">
                        {{
                            edit_item?.id
                                ? t('batchTools.editProject')
                                : t('batchTools.addProject')
                        }}
                    </h2>
                    <span
                        class="text-2xl cursor-pointer hover:text-text-active transition-all duration-300 hover:scale-110"
                        @click="dialogVisible = false"
                    >
                        ×
                    </span>
                </div>

                <div class="w-full">
                    <el-input
                        class="h-[44px]"
                        v-model="edit_item.name"
                        :placeholder="t('batchTools.placeholderName')"
                    />
                </div>

                <div
                    class="w-full absolute bottom-5 p-5 flex items-center justify-center gap-2"
                >
                    <el-button
                        @click="dialogVisible = false"
                        class="hover:!border-white/30 hover:!bg-transparent hover:!text-white"
                        >{{ t('batchTools.cancel') }}</el-button
                    >
                    <el-button
                        :loading="loading"
                        type="primary"
                        @click="handleSubmitProject()"
                        class="!text-black hover:!bg-[var(--active-btn)] hover:!border-transparent"
                        >{{ t('batchTools.confirm') }}</el-button
                    >
                </div>
            </div>
        </div>
    </transition>

    <!-- delete modal (aligned with ProjectTab.vue) -->
    <el-dialog
        v-model="deleteDialogVisible"
        :title="t('batchTools.deleteConfirmTitle')"
        width="92%"
    >
        <p>{{ t('batchTools.deleteConfirmMessage') }}</p>
        <template #footer>
            <el-button
                class="hover:!border-white/30 hover:!bg-transparent hover:!text-white"
                @click="deleteDialogVisible = false"
                >{{ t('batchTools.cancel') }}</el-button
            >
            <el-button
                class="hover:!bg-[var(--active-btn)] !text-black hover:!border-transparent"
                :loading="deleteLoading"
                type="primary"
                @click="handleConfirmDelete()"
                >{{ t('batchTools.confirm') }}</el-button
            >
        </template>
    </el-dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAppStore } from '@/stores/app'
import { useI18n } from '@/composables/useI18n'
import { noticeMessagePopup } from '~/utils/common'

const appStore = useAppStore()
const { t } = useI18n()

/**
 * Same filtering rules as `components/Tool/ProjectTab.vue`:
 * 1) remove null/undefined
 * 2) de-duplicate by id (keep first)
 * 3) if >1 project, hide id === -1 ("Untitled Project")
 */
const filteredProjectList = computed(() => {
    if (!appStore.projectList || !Array.isArray(appStore.projectList)) {
        return []
    }

    let filtered = appStore.projectList.filter(
        (tab: any) => tab != null && tab.id != null
    )

    const seenIds = new Set()
    filtered = filtered.filter((tab: any) => {
        if (seenIds.has(tab.id)) return false
        seenIds.add(tab.id)
        return true
    })

    if (filtered.length > 1) {
        filtered = filtered.filter((tab: any) => tab.id !== -1)
    }

    return filtered
})

const currentProjectName = computed(() => {
    return (
        appStore.currentProject?.name ||
        filteredProjectList.value?.[0]?.name ||
        'Untitled Project'
    )
})

onMounted(async () => {
    if (!appStore.projectList?.length) {
        await appStore.getProjectList()
    }
})

// add/edit/delete modals (aligned with ProjectTab.vue)
const dialogVisible = ref(false)
const deleteDialogVisible = ref(false)
const deleteLoading = ref(false)
const loading = ref(false)

const initProjectItem = () => ({ id: null, name: '' })
const edit_item = ref<any>(initProjectItem())
const tabToDelete = ref<any>(null)

const handleAddClick = () => {
    dialogVisible.value = true
    edit_item.value = initProjectItem()
}

// Requirement: if current project id === -1, "edit" behaves like "add"
const handleEditCurrent = () => {
    const tab = appStore.currentProject
    if (!tab) return

    if (tab.id === -1) {
        dialogVisible.value = true
        edit_item.value = { id: null, name: tab.name || 'Untitled Project' }
        return
    }

    dialogVisible.value = true
    edit_item.value = { id: tab.id, name: tab.name }
}

const handleDeleteCurrent = () => {
    const tab = appStore.currentProject
    if (!tab) return
    if (filteredProjectList.value.length <= 1) return
    tabToDelete.value = tab
    deleteDialogVisible.value = true
}

const handleSubmitProject = async () => {
    if (!edit_item.value?.name?.trim()) {
        noticeMessagePopup(
            t('batchTools.placeholderName'),
            t('common.tip'),
            'warning',
            5000,
            ''
        )
        return
    }

    loading.value = true
    try {
        const is_edit = edit_item.value?.id && edit_item.value?.id !== -1
        const ok: boolean = is_edit
            ? await appStore.updateProject(edit_item.value?.id || '', edit_item.value)
            : await appStore.createProject({ name: edit_item.value.name })

        if (ok) {
            dialogVisible.value = false
            edit_item.value = initProjectItem()
        }
    } finally {
        loading.value = false
    }
}

const handleConfirmDelete = async () => {
    if (!tabToDelete.value?.id) {
        deleteDialogVisible.value = false
        return
    }

    deleteLoading.value = true
    try {
        const ok = await appStore.deleteProject(tabToDelete.value.id)
        if (ok) {
            deleteDialogVisible.value = false
            tabToDelete.value = null
        }
    } finally {
        deleteLoading.value = false
    }
}

const handleProjectDropdownCommand = (cmd: any) => {
    if (!cmd?.type) return

    if (cmd.type === 'select') {
        const p = cmd.project
        if (!p?.id) return
        // Only change current project (refresh dependent content)
        appStore.changeCurrentProject(p)
        return
    }

    if (cmd.type === 'add') return handleAddClick()
    if (cmd.type === 'edit') return handleEditCurrent()
    if (cmd.type === 'delete') return handleDeleteCurrent()
}
</script>

